import { useRef } from 'react';

import Box from '@zenbusiness/zui/Box';
import DialogContent from '@zenbusiness/zui/DialogContent';
import Paper from '@zenbusiness/zui/Paper';
import PropTypes from 'prop-types';
import { useReactToPrint } from 'react-to-print';

import FormInfoModal from '#client/components/FormInfoModal';
import { ActionInfoButtonsGrid } from '#client/components/Grid';
import {
  TRANSACTION_DIRECTION,
  TRANSACTION_TYPE_WITH_CHECKING_ACCOUNT
} from '#client/constants/transaction';
import useBreakpoints from '#client/hooks/useBreakpoints';
import useModalDisplayTracker from '#client/hooks/useModalDisplayTracker';
import useUnitAccount from '#client/hooks/useUnitAccount';

import AdditionalInfo from './components/AdditionalInfo';
import ATMDetail from './components/ATMDetail';
import DetailWrapper from './components/DetailWrapper';
import FXDetail from './components/FXDetail';
import MerchantDetail from './components/MerchantDetail';
import TransactionDetail from './components/TransactionDetail';
import UserDetail from './components/UserDetail';
import { ButtonStyled, PrintedBox } from './TransactionDetailModal.style';

export default function TransactionDetailModal({
  amount,
  createdAt,
  type,
  summary,
  from,
  to,
  balanceAmount,
  traceNumber,
  reason,
  id,
  cardId,
  direction,
  paymentMethod,
  digitalWallet,
  cardPresent,
  recurring,
  verificationMethod,
  merchantName,
  merchantLocation,
  merchantCategory,
  atmName,
  atmLocation,
  surcharge,
  currencyConversion,
  handleClose,
  description,
  counterpartyIdFromTransaction
}) {
  const contentRef = useRef(null);
  const {
    sizes: { isDesktop }
  } = useBreakpoints();
  const { unitAccount } = useUnitAccount();

  const handlePrint = useReactToPrint({
    content: () => contentRef.current
  });

  useModalDisplayTracker({
    elementName: 'transactions_details_modal',
    elementText: 'transactions_details_modal',
    appPageName: 'transactions_details_modal'
  });

  return (
    <FormInfoModal
      borderRadius={isDesktop ? '16px' : '0'}
      fullScreen={!isDesktop}
      handleClose={handleClose}
      title={summary}
    >
      <DialogContent sx={{ padding: 0 }}>
        <DetailWrapper>
          <PrintedBox ref={contentRef}>
            <Paper variant="outlined">
              <TransactionDetail
                amount={amount}
                balanceAmount={balanceAmount}
                cardId={cardId}
                createdAt={createdAt}
                description={description}
                direction={direction}
                id={id}
                merchantName={merchantName}
                reason={reason}
                traceNumber={traceNumber}
                type={type}
              />
              {TRANSACTION_TYPE_WITH_CHECKING_ACCOUNT.includes(type) &&
              direction === TRANSACTION_DIRECTION.CREDIT ? (
                <UserDetail
                  accountHolder={unitAccount?.name}
                  accountNumber={unitAccount?.accountNumber}
                  bank="ZenBusiness"
                  fullAccountNumber={unitAccount?.accountNumber}
                  title="To"
                />
              ) : (
                <UserDetail
                  accountHolder={to?.accountHolder}
                  accountNumber={to?.fullAccountNumber}
                  counterpartyIdFromTransaction={counterpartyIdFromTransaction}
                  fullAccountNumber={to?.fullAccountNumber}
                  routingNumber={to?.routingNumber}
                  title="To"
                />
              )}
              {TRANSACTION_TYPE_WITH_CHECKING_ACCOUNT.includes(type) &&
              direction === TRANSACTION_DIRECTION.DEBIT ? (
                <UserDetail
                  accountHolder={unitAccount?.name}
                  accountNumber={unitAccount?.accountNumber}
                  bank="ZenBusiness"
                  fullAccountNumber={unitAccount?.accountNumber}
                  title="From"
                />
              ) : (
                <UserDetail
                  accountHolder={from?.accountHolder}
                  accountNumber={from?.fullAccountNumber}
                  counterpartyIdFromTransaction={counterpartyIdFromTransaction}
                  fullAccountNumber={from?.fullAccountNumber}
                  routingNumber={from?.routingNumber}
                  title="From"
                />
              )}
              <MerchantDetail
                cardPresent={cardPresent}
                digitalWallet={digitalWallet}
                merchantCategory={merchantCategory}
                merchantLocation={merchantLocation}
                merchantName={merchantName}
                paymentMethod={paymentMethod}
                recurring={recurring}
                verificationMethod={verificationMethod}
              />
              <ATMDetail
                atmLocation={atmLocation}
                atmName={atmName}
                surcharge={surcharge}
              />
              <FXDetail currencyConversion={currencyConversion} />
            </Paper>
          </PrintedBox>
          <AdditionalInfo handlePrint={handlePrint} />
        </DetailWrapper>
      </DialogContent>
      {!isDesktop && (
        <ActionInfoButtonsGrid>
          <Box>
            <ButtonStyled
              color="primary"
              onClick={handleClose}
              size="large"
              variant="text"
            >
              Close
            </ButtonStyled>
          </Box>
        </ActionInfoButtonsGrid>
      )}
    </FormInfoModal>
  );
}

TransactionDetailModal.propTypes = {
  // Transaction Details
  amount: PropTypes.string,
  createdAt: PropTypes.string,
  type: PropTypes.string,
  summary: PropTypes.string,
  balanceAmount: PropTypes.string,
  id: PropTypes.string,
  traceNumber: PropTypes.string,
  reason: PropTypes.string,
  cardId: PropTypes.string,
  direction: PropTypes.string,
  description: PropTypes.string,
  // From Details
  from: PropTypes.shape({
    accountHolder: PropTypes.string,
    routingNumber: PropTypes.string,
    fullAccountNumber: PropTypes.string,
    accountNumber: PropTypes.string
  }),
  // To Details
  to: PropTypes.shape({
    accountHolder: PropTypes.string,
    routingNumber: PropTypes.string,
    fullAccountNumber: PropTypes.string,
    accountNumber: PropTypes.string
  }),
  // Merchant Detail
  paymentMethod: PropTypes.string,
  digitalWallet: PropTypes.string,
  cardPresent: PropTypes.bool,
  recurring: PropTypes.bool,
  verificationMethod: PropTypes.string,
  merchantName: PropTypes.string,
  merchantLocation: PropTypes.string,
  merchantCategory: PropTypes.string,
  // ATM Details
  atmName: PropTypes.string,
  atmLocation: PropTypes.string,
  surcharge: PropTypes.string,
  // FX Details
  currencyConversion: PropTypes.shape({
    originalCurrency: PropTypes.string,
    amountInOriginalCurrency: PropTypes.number,
    fxRate: PropTypes.string
  }),
  handleClose: PropTypes.func,
  counterpartyIdFromTransaction: PropTypes.string
};
